
import React, { useEffect, useState } from 'react';
import { View, TouchableOpacity, useWindowDimensions, FlatList, RefreshControl, StyleSheet, } from 'react-native';
import moment from 'moment';

import { appColors, fontSize } from '../../../themes/appTheme';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useAppNavigation, useAppRoute } from '../../../types/@react-navigation/navigation';
import { getAllCustomerReport } from '../../../redux/slices/customerReportSlice'


import { ZenBadge, ZenDateTimePicker, ZenIcon, ZenLoader, ZenText, ZenEmptyComponent, ZenToast, } from 'zen-core-elements';
import CommonHeader from '../../../shared/commonComponents/commonHeader';
import CommonService from '../../../shared/services/commonService';
import { Divider } from '@rneui/base';
import { appFonts } from 'zen-core-elements/src/shared/constants';

type order = {
    date: string,
    orderCount: number,
    totalSales: number,
    totalDiscount: number,
    netSales: number,
    returnedOrCancelledOrders: number
}
type refund = {
    date: string,
    refundAmount: number
}
type customerRow = {
    customerId: number,
    customerName: string,
    email: string,
    phoneNumber: string,
    customerType: string,
    order: order[]
    refund: refund[]
    totalOrderCount: number,
    totalReturnedOrCancelled: number,
    totalSales: number,
    totalDiscount: number,
    totalNetSales: number,
    totalRefund: number
}

const ReportListItem = ({ item, currencySymbol }: { item: customerRow, currencySymbol: string }) => {
    return (
        <View style={styles.reportItem}>
            <View style={styles.reportItemHeader}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <ZenText content={item.customerName ?? 'N/A'} bold size={fontSize?.largerFontSize} />
                    <ZenBadge
                        badgeName={`ID : ${item.customerId ?? 'N/A'}`}
                        badgeColor={'#F1F5F9'}
                        badgeTextColor={appColors.lightDark}
                        badgeContainerStyle={{ paddingVertical: 2, paddingHorizontal: 8, borderRadius: 8 }}
                    />
                </View>
                <ZenBadge
                    badgeName={item.customerType ?? 'N/A'}
                    badgeColor={item.customerType == "Verified" ? appColors?.badgeColor : appColors?.inActiveColor}
                    badgeTextColor={appColors.light}
                    badgeTextSize={"small"}
                    badgeTextStyle={{ fontFamily: appFonts?.bold }}
                    badgeContainerStyle={{ paddingVertical: 2, paddingHorizontal: 8, borderRadius: 8 }}
                />
            </View>

            <Divider style={{ marginVertical: 12, backgroundColor: '#F1F5F9' }} />

            <View style={styles.metricsGrid}>
                <View style={[styles.metricItem, { flex: 2, minWidth: 150 }]}>
                    <ZenText content="Email" size={fontSize?.tinyFontSize} color={appColors.lightDark} style={{ marginBottom: 4 }} />
                    <ZenText content={item.email ?? 'N/A'} bold size={fontSize?.mediumFontSize} color={appColors.dark} />
                </View>
                <View style={[styles.metricItem, { flex: 1.5, minWidth: 120 }]}>
                    <ZenText content="Phone No" size={fontSize?.tinyFontSize} color={appColors.lightDark} style={{ marginBottom: 4 }} />
                    <ZenText content={item.phoneNumber?.length > 0 ? item?.phoneNumber : 'N/A'} bold size={fontSize?.mediumFontSize} color={appColors.dark} />
                </View>
                <View style={[styles.metricItem, { flex: 1, minWidth: 80 }]}>
                    <ZenText content="Orders" size={fontSize?.tinyFontSize} color={appColors.lightDark} style={{ marginBottom: 4 }} />
                    <ZenText content={`${item.totalOrderCount ?? 0}`} bold size={fontSize?.mediumFontSize} color={appColors.dark} />
                </View>
                <View style={[styles.metricItem, { flex: 1, minWidth: 80 }]}>
                    <ZenText content="Returns" size={fontSize?.tinyFontSize} color={appColors.lightDark} style={{ marginBottom: 4 }} />
                    <ZenBadge
                        badgeName={String(item.totalReturnedOrCancelled ?? 0)}
                        badgeTextColor={item.totalReturnedOrCancelled > 0 ? '#EF4444' : '#64748B'}
                        badgeColor={item.totalReturnedOrCancelled > 0 ? '#FEE2E2' : '#F1F5F9'}
                        badgeContainerStyle={{ paddingVertical: 2, paddingHorizontal: 8, borderRadius: 6 }}
                    />
                </View>
                <View style={[styles.metricItem, { flex: 1.2, minWidth: 100 }]}>
                    <ZenText content="Net Sales" size={fontSize?.tinyFontSize} color={appColors.lightDark} style={{ marginBottom: 4 }} />
                    <ZenText content={CommonService.currencyStandard(currencySymbol, item.totalNetSales?.toString() ?? "0")} bold size={fontSize?.mediumFontSize} color={appColors.primary} />
                </View>
            </View>
        </View>
    );
};

const limit = 10;

const CustomerReportPage = () => {
    const { width, height } = useWindowDimensions();
    const { id: locationId } = useAppSelector(state => state?.common?.currentLocation)

    const dispatch = useAppDispatch();
    const navigation = useAppNavigation();
    const route = useAppRoute("CustomerReport");
    const { loading: reduxLoading, error, value, count: totalCount, filter: reduxFilter } = useAppSelector((s) => s?.customerReport?.customerReportData);
    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
    const [datePickerVisibile, setDatePickerVisible] = useState<{ visibilty: boolean; current: 'from' | 'to' | null }>({ visibilty: false, current: null });
    const [dateRange, setDateRange] = useState<{ from: string | null; to: string | null }>({
        from: route?.params?.beginDate!,
        to: route?.params?.endDate!
    });
    const [listRefresh, setListRefresh] = useState<boolean>(false);
    const [offset, setOffset] = useState<number>(0);
    const [footerLoader, setFooterLoader] = useState<boolean>(false)

    useEffect(() => {
        setOffset(0);
        fetchCustomerReports(0);
    }, [dateRange]);

    // const reInitializeStates=()=>{
    //     setDateRange({ from: route?.params?.beginDate!, to: route?.params?.endDate! });
    //         setOffset(0);
    // }
    const fetchCustomerReports = async (offsetParam: number, forceRefresh = false) => {
        const filterData = JSON.stringify({ customDateRange: { begin: dateRange?.from, end: dateRange?.to } });

        if (!forceRefresh && offsetParam === 0 && filterData === reduxFilter && value?.length > 0) {
            return;
        }

        dispatch(getAllCustomerReport({
            data: {
                limit: limit,
                offset: offsetParam,
                filterData: filterData,
                locationId: locationId
            }
        })).unwrap().then((res) => {
            setListRefresh(false);
            setFooterLoader(false);
        }).catch((err) => {
            setListRefresh(false);
            setFooterLoader(false);
            ZenToast({ type: 'error', message: err?.message ?? 'Something went wrong' });
        });
    };

    const customerRows: customerRow[] = (value ?? []) as customerRow[];

    useEffect(() => {
        if (error) {
            ZenToast({ type: 'error', message: error });
        }
    }, [error]);

    const onEndReached = () => {
        if (!footerLoader && !reduxLoading && totalCount > offset + limit) {
            setFooterLoader(true);
            const next = offset + limit;
            setOffset(next);
            fetchCustomerReports(next);
        }
    };

    const onRefresh = () => {
        setListRefresh(true);
        setDateRange({ from: route?.params?.beginDate!, to: route?.params?.endDate! });
        setOffset(0);
        fetchCustomerReports(0, true);
    };


    const DateFilterBar = () => (
        <View style={styles.unifiedBar}>
            <TouchableOpacity style={styles.dateSegment} onPress={() => setDatePickerVisible({ visibilty: true, current: 'from' })}>
                <ZenIcon type='material-community' name="calendar-range" size={20} color={appColors.primary} />
                <ZenText content={dateRange?.from ? moment(dateRange?.from).format('DD MMM, YYYY') : 'Select From'} size={fontSize.tinyFontSize} color={appColors.primary} />
            </TouchableOpacity>

            <View style={styles.verticalDivider} />

            <TouchableOpacity style={styles.dateSegment} onPress={() => setDatePickerVisible({ visibilty: true, current: 'to' })}>
                <ZenText content={dateRange?.to ? moment(dateRange?.to).format('DD MMM, YYYY') : 'Select To'} size={fontSize.tinyFontSize} color={appColors.primary} />
                <ZenIcon type='material-community' name="chevron-down" size={18} color={appColors.primary} />
            </TouchableOpacity>
        </View>
    );

    return (
        <View style={styles.safeArea}>
            <CommonHeader headerName="Customers Report" backIcon backIconOnpress={() => navigation.goBack()} />
            <DateFilterBar />
            {datePickerVisibile?.visibilty && (
                <ZenDateTimePicker
                    date={
                        datePickerVisibile?.current === 'from' ?
                            dateRange?.from ? new Date(dateRange?.from) : new Date()
                            : dateRange?.to ? new Date(dateRange?.to) : new Date()
                    }
                    mode="date"
                    open={datePickerVisibile?.visibilty}
                    maximumDate={datePickerVisibile?.current == 'from' && dateRange?.to ? new Date(dateRange?.to) : new Date()}
                    minimumDate={datePickerVisibile?.current !== 'from' && dateRange?.from ? new Date(dateRange?.from) : undefined}
                    onConfirm={(data) => {
                        if (datePickerVisibile?.current == 'from') {
                            if (!dateRange?.to) {
                                setDateRange((prev) => ({ ...prev, to: moment(new Date()).format('YYYY-MM-DD') }));
                            }
                            setDateRange((prev) => ({ ...prev, from: moment(data).format('YYYY-MM-DD') }));
                            setDatePickerVisible({ visibilty: false, current: null });
                        } else {
                            setDateRange((prev) => ({ ...prev, to: moment(data).format('YYYY-MM-DD') }));
                            setDatePickerVisible({ visibilty: false, current: null });
                            if (!dateRange?.from) {
                                setDateRange((prev) => ({ ...prev, from: moment(new Date()).format('YYYY-MM-DD') }));
                            }
                        }
                    }}
                    onCancel={() => setDatePickerVisible({ visibilty: false, current: null })}
                />
            )}

            {(reduxLoading && !value) ? (
                <ZenLoader viewStyle={{}} color={appColors.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
            ) : (
                <View style={styles.contentContainer}>
                    <FlatList
                        data={customerRows}
                        keyExtractor={(item, index) => `${item.customerId ?? ''}-${item.email ?? ''}-${index}`}
                        renderItem={({ item }) => <ReportListItem item={item} currencySymbol={currencySymbol} />}
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={{ paddingHorizontal: 15, gap: 12, flexGrow: customerRows.length ? undefined : 1, paddingBottom: 90 }}
                        ListEmptyComponent={<ZenEmptyComponent />}
                        onEndReached={onEndReached}
                        onEndReachedThreshold={0.2}
                        ListFooterComponent={
                            footerLoader ? (
                                <View style={{ padding: 12, alignItems: 'center' }}>
                                    <ZenLoader size="small" color={appColors.primary} />
                                </View>
                            ) : undefined
                        }
                        refreshControl={<RefreshControl refreshing={listRefresh} onRefresh={onRefresh} />}
                    />
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: appColors.lightBackgroundColor },
    unifiedBar: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFF',
        borderRadius: 16,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        alignSelf: 'flex-end',
        marginHorizontal: 15,
        marginVertical: 15
    },
    dateSegment: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, gap: 8 },
    verticalDivider: { width: 1.5, height: 22, backgroundColor: '#E2E8F0', marginHorizontal: 4 },
    contentContainer: {
        flex: 1,
    },
    card: {
        flex: 1,
        margin: 8,
        backgroundColor: '#FFF',
        padding: 24,
        borderRadius: 20,
        flexDirection: 'row',
        alignItems: 'center',
        shadowColor: '#000',
        shadowOpacity: 0.04,
        shadowRadius: 10,
        elevation: 3
    },
    iconContainer: { padding: 14, borderRadius: 16, marginRight: 18 },
    tableTitleRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15, paddingHorizontal: 5 },
    reportItem: {
        backgroundColor: appColors.light,
        padding: 18,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        shadowColor: '#000',
        shadowOpacity: 0.02,
        shadowRadius: 8,
        elevation: 2,
    },
    reportItemHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    metricsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    metricItem: {
        marginBottom: 10,
        alignItems: 'flex-start'
    },
});

export default CustomerReportPage;




